
/**
 * Whitelabel list/detail view helpers.
 */

export type WhitelabelDetailViewMode =
  | "inProgressPayPending"
  | "inProgressPayDone"
  | "failed"
  | "success";

export type WhitelabelListItemFields = {
  whitelabelId?: string;
  whitelabelName?: string;
  subdomain?: string;
  whitelabelStatus?: string;
  pendingPayment?: boolean;
  /** List API: payment still required (drives “complete your payment” on draft cards). */
  paymentPending?: boolean;
  paymentCompleted?: boolean;
  packageId?: string;
  balance?: number | string;
  failureReason?: string;
  publicUrl?: string;
  /** List API: full site URL; UI may strip `https://` for display. */
  webUrl?: string;
  siteUrl?: string;
  isDraft?: boolean;
  /** Unix time in seconds when the row was created (used for draft list countdown). */
  createdAt?: number | string;
  packageName?: string;
};

export function normalizeWlStatus(raw: unknown): string {
  return String(raw ?? "").trim().toUpperCase();
}

function normalizeBooleanFlag(x: unknown): boolean | null {
  if (x === true || x === "true" || x === 1 || x === "1") return true;
  if (x === false || x === "false" || x === 0 || x === "0") return false;
  return null;
}

/** Workflow status from root `whitelabelStatus`. */
export function getWhitelabelItemStatusRaw(
  item: WhitelabelListItemFields | Record<string, unknown>
): unknown {
  const o = item as Record<string, unknown>;
  const root = o.whitelabelStatus;
  if (root !== undefined && root !== null && String(root).trim() !== "") return root;
  return undefined;
}

/** True when user still owes payment for the selected plan (only meaningful for IN_PROGRESS). */
export function isWlPlanPaymentPending(item: WhitelabelListItemFields): boolean {
  const st = normalizeWlStatus(getWhitelabelItemStatusRaw(item));
  if (st !== "IN_PROGRESS") return false;
  const p = normalizeBooleanFlag(item.pendingPayment);
  const c = normalizeBooleanFlag(item.paymentCompleted);
  if (c === true) return false;
  if (p === false) return false;
  if (p === true) return true;
  if (c === false) return true;
  return true;
}

export function hasUnpaidInProgressWhitelabel(items: WhitelabelListItemFields[]): boolean {
  return items.some(
    (it) =>
      normalizeWlStatus(getWhitelabelItemStatusRaw(it)) === "IN_PROGRESS" &&
      isWlPlanPaymentPending(it)
  );
}

export function getWlFailureReason(d: WhitelabelListItemFields | Record<string, unknown>): string {
  const o = d as Record<string, unknown>;
  const r = o.failureReason ?? o.rejectionReason;
  const s = String(r ?? "").trim();
  return s;
}

export function getWlPublicUrl(d: WhitelabelListItemFields | Record<string, unknown>): string {
  const o = d as Record<string, unknown>;
  const explicit = o.publicUrl ?? o.siteUrl;
  const ex = String(explicit ?? "").trim();
  if (ex) return ex;
  const sub = String(o.subdomain ?? "").trim().toLowerCase();
  if (!sub) return "";
  return `https://${sub}.whitelabel.com`;
}

export type FormatWlBalanceOptions = {
  /** Whole numbers only (no fractional digits). */
  integerDisplay?: boolean;
};

/** List + detail wallet amount: read only API `balance`. */
export function formatWlBalance(
  item: WhitelabelListItemFields | Record<string, unknown>,
  options?: FormatWlBalanceOptions
): string {
  const raw = (item as Record<string, unknown>).balance;
  if (raw === null || raw === undefined || raw === "") return "0";
  const trimmed = String(raw).trim();
  if (trimmed === "") return "0";
  const n = Number(trimmed);
  if (!Number.isNaN(n)) {
    if (options?.integerDisplay) {
      return Math.round(n).toLocaleString(undefined, {
        maximumFractionDigits: 0,
        minimumFractionDigits: 0,
      });
    }
    return n.toLocaleString();
  }
  return String(raw);
}

/** Root-level display name from `whitelabelName`. */
export function getWhitelabelListDisplayName(
  item: WhitelabelListItemFields | Record<string, unknown>
): string {
  const o = item as Record<string, unknown>;
  return String(o.whitelabelName ?? "").trim();
}

export type WhitelabelListStatusBadge = {
  label: string;
  /** Text color only (no chip outline). */
  className: string;
};

export function getListStatusBadge(item: WhitelabelListItemFields): WhitelabelListStatusBadge {
  const st = normalizeWlStatus(getWhitelabelItemStatusRaw(item));
  if (st === "SUCCESS") {
    return { label: "Live", className: "text-emerald-300" };
  }
  if (st === "FAILED") {
    return { label: "Failed", className: "text-red-300" };
  }
  if (st === "INITIATED") {
    return { label: "Initiated", className: "text-violet-200" };
  }
  if (st === "IN_PROGRESS") {
    if (isWlPlanPaymentPending(item)) {
      return { label: "Awaiting payment", className: "text-amber-200" };
    }
    return { label: "Processing", className: "text-sky-300" };
  }
  if (st === "PENDING") {
    return { label: "Pending", className: "text-amber-200" };
  }
  return { label: st || "Unknown", className: "text-zinc-400" };
}

export function getWhitelabelDetailViewMode(
  details: WhitelabelListItemFields | null
): WhitelabelDetailViewMode {
  if (!details) return "inProgressPayPending";
  const st = normalizeWlStatus(getWhitelabelItemStatusRaw(details));
  if (st === "SUCCESS") return "success";
  if (st === "FAILED") return "failed";
  if (st === "IN_PROGRESS") {
    return isWlPlanPaymentPending(details) ? "inProgressPayPending" : "inProgressPayDone";
  }
  return "inProgressPayPending";
}

/** Live brand fields from POST /whitelabel/get-whitelabel-details (detail page for non-draft rows). */
export type WhitelabelLiveDetails = {
  logoUrl?: string;
  webUrl?: string;
  telegramUrl?: string;
  balance?: number | string;
  /** Filled from API `adminUrl` / `adminUserId` / `adminPassword` for UI. */
  portalUrl?: string;
  portalUserId?: string;
  portalPassword?: string;
};

const strTrim = (v: unknown): string => (v != null ? String(v).trim() : "");

/**
 * Parse RS_OK payload from `/whitelabel/get-whitelabel-details`.
 * Expects `whitelabel` (or root): logoUrl, webUrl, telegramLink, balance, adminUrl, adminUserId, adminPassword.
 */
export function parseWhitelabelLiveDetailsResponse(apiData: unknown): WhitelabelLiveDetails | null {
  if (!apiData || typeof apiData !== "object") return null;
  const d = apiData as Record<string, unknown>;
  if (d.status !== "RS_OK") return null;
  const raw = d.whitelabel;
  const o =
    raw && typeof raw === "object" && !Array.isArray(raw)
      ? (raw as Record<string, unknown>)
      : d;
  const logoUrl = strTrim(o.logoUrl);
  const webUrl = strTrim(o.webUrl);
  const telegramUrl = strTrim(o.telegramLink);
  const portalUrl = strTrim(o.adminUrl);
  const portalUserId = strTrim(o.adminUserId);
  const portalPassword = strTrim(o.adminPassword);
  const balRaw = o.balance;
  const out: WhitelabelLiveDetails = {};
  if (logoUrl) out.logoUrl = logoUrl;
  if (webUrl) out.webUrl = webUrl;
  if (telegramUrl) out.telegramUrl = telegramUrl;
  if (portalUrl) out.portalUrl = portalUrl;
  if (portalUserId) out.portalUserId = portalUserId;
  if (portalPassword) out.portalPassword = portalPassword;
  if (balRaw !== undefined && balRaw !== null && String(balRaw).trim() !== "") {
    out.balance = balRaw as number | string;
  }
  /** Empty object still means RS_OK — list row can fall back to list payload fields. */
  return out;
}

/** Prefix http(s) for display links when the API omits the scheme. */
export function whitelabelExternalHref(raw: string): string {
  const t = String(raw ?? "").trim();
  if (!t) return "";
  if (/^https?:\/\//i.test(t)) return t;
  if (/^t\.me\//i.test(t) || /^telegram\.me\//i.test(t)) return `https://${t}`;
  return `https://${t}`;
}
